import { site } from './shared';
import type { LegalPageContent } from './types';

const addressBlockEn = [
  site.name,
  site.addressStreet,
  `${site.addressPostalCode} ${site.addressLocality}`,
  site.addressCountryEn,
];

export const legalNoticeEn: LegalPageContent = {
  locale: 'en',
  title: 'Legal Notice | Maxim Domentii',
  description:
    'Legal notice, operator details and contact information for maximdomentii.com.',
  h1: 'Legal Notice',
  intro: 'This page provides operator and business identity information for this website.',
  sections: [
    {
      title: 'Operator of this website',
      contactBlock: [
        ...addressBlockEn,
        `Email: ${site.email}`,
        `Phone: ${site.phoneDisplay}`,
        `Website: ${site.domain}`,
        `Company UID: ${site.uid}`,
        `VAT number: ${site.vat}`,
      ],
    },
    {
      title: 'Responsible for content',
      contactBlock: addressBlockEn,
    },
    {
      title: 'Purpose of the website',
      paragraphs: [
        'This website presents software engineering, software architecture, SaaS development, AI automation and digital consulting services offered by Maxim Domentii.',
      ],
    },
    {
      title: 'Copyright',
      paragraphs: [
        'Unless otherwise stated, the content and design of this website are owned by Maxim Domentii. Third-party trademarks, logos and names remain the property of their respective owners.',
      ],
    },
    {
      title: 'Contact',
      paragraphs: [`For questions about this website, please contact ${site.email}.`],
    },
  ],
};

export const dataProtectionEn: LegalPageContent = {
  locale: 'en',
  title: 'Data Protection | Maxim Domentii',
  description:
    'Data protection notice explaining contact form, reCAPTCHA, hosting, local storage and website data processing on maximdomentii.com.',
  h1: 'Data Protection',
  intro: 'Last updated: 30 April 2026',
  sections: [
    {
      title: '1. Controller',
      paragraphs: ['The controller responsible for data processing on this website is:'],
      contactBlock: [...addressBlockEn, `Email: ${site.email}`, `Phone: ${site.phoneDisplay}`],
    },
    {
      title: '2. Scope',
      paragraphs: [
        'This data protection notice explains how personal data is processed when you visit maximdomentii.com, use the contact form, or communicate with Maxim Domentii in connection with the services offered on this website.',
      ],
    },
    {
      title: '3. Personal data processed',
      paragraphs: ['This website processes the following personal data in the ways described below:'],
      list: [
        'Contact form data: name, email address, phone number when entered, project message, reCAPTCHA token and submission metadata handled by the website backend.',
        'Communication data: information you provide by email, phone or other direct communication.',
        'Technical access data: IP address, browser type, device information, operating system, requested pages, date and time of access, referrer URL and server log data processed by the hosting infrastructure.',
        'Security verification data: information processed through Google reCAPTCHA v2 to verify the challenge and protect the form against spam and abuse.',
        'Preference data: selected language stored in local storage under the website language preference key.',
      ],
    },
    {
      title: '4. Purposes of processing',
      paragraphs: ['Personal data is processed for the following purposes:'],
      list: [
        'responding to enquiries and project requests;',
        'preparing and providing software engineering, architecture, AI automation and consulting services;',
        'operating, securing and improving the website;',
        'preventing spam, abuse and unauthorised access;',
        'complying with legal obligations and protecting legal claims where required.',
      ],
    },
    {
      title: '5. Contact form',
      paragraphs: [
        'When you submit the contact form, the data you provide is sent to the website backend and used to process your enquiry. The frontend sends only the fields name, email, phone, text and the reCAPTCHA token to the contact API. The form requires a reCAPTCHA verification token to reduce spam and abuse. Please do not submit confidential or sensitive information through the contact form.',
      ],
    },
    {
      title: '6. Google reCAPTCHA',
      paragraphs: [
        "This website uses Google reCAPTCHA v2 (checkbox) to protect the contact form from automated abuse. The reCAPTCHA widget is loaded for the contact form when the form is near the viewport or when a user focuses the form. Google processes technical and usage-related information such as IP address, browser and device information, interaction signals and other data required for the security check. The exact data processed is determined by Google's implementation and the user's browser environment.",
      ],
    },
    {
      title: '7. Hosting and technical infrastructure',
      paragraphs: [
        'This website is hosted as a static website on Amazon Web Services in the AWS Europe (Zurich) Region. Amazon S3 is used for static website storage and Amazon CloudFront is used for content delivery. AWS and related technical infrastructure providers process technical access data and server logs to deliver the website securely and reliably.',
      ],
    },
    {
      title: '8. Cookies, local storage and similar technologies',
      paragraphs: [
        'This website does not use analytics cookies, marketing cookies, tracking pixels or advertising tags. It uses local storage to remember the selected language so visitors can return to the preferred language version.',
        'Google reCAPTCHA and hosting or security services use cookies or similar technologies that are necessary for spam protection, abuse prevention, security and reliable website delivery.',
      ],
    },
    {
      title: '9. Disclosure to third parties',
      paragraphs: [
        'Personal data is disclosed to service providers required for website operation, hosting, spam protection, communication, maintenance and legal compliance. These providers include Amazon Web Services for hosting and content delivery, Google reCAPTCHA for spam protection and email or communication providers used to respond to enquiries.',
      ],
    },
    {
      title: '10. Transfers abroad',
      paragraphs: [
        'The static website files are stored in Switzerland in the AWS Europe (Zurich) Region and served from that region as the website origin. Other service providers used by this website, including Google reCAPTCHA, Amazon CloudFront edge infrastructure and email or communication providers, process data in Switzerland, the European Economic Area, the United States and other countries. Transfers to countries without an adequate level of data protection are handled with safeguards required by applicable law, such as contractual safeguards used by the relevant service providers.',
      ],
    },
    {
      title: '11. Retention',
      paragraphs: [
        'Personal data is kept only as long as needed for the purposes described above, unless a longer retention period is required or permitted by law. Contact enquiries are retained for follow-up, business administration and legal documentation. Technical logs are retained for a limited period for security and operational purposes.',
      ],
    },
    {
      title: '12. Data security',
      paragraphs: [
        'Appropriate technical and organisational measures are used to protect personal data against unauthorised access, loss, misuse or alteration. No internet transmission or electronic storage method can be guaranteed to be completely secure.',
      ],
    },
    {
      title: '13. Your rights',
      paragraphs: [
        'Subject to applicable data protection law, you have rights regarding your personal data, including the right to request information, correction of inaccurate data, deletion, restriction of processing, objection to processing and data portability. You also have the right to lodge a complaint with a competent data protection authority where applicable.',
        `For requests, please contact: ${site.email}.`,
      ],
    },
    {
      title: '14. Automated decisions',
      paragraphs: [
        'This website does not make automated individual decisions with legal effects or similarly significant effects. Google reCAPTCHA automatically assesses whether a form submission appears legitimate for security purposes.',
      ],
    },
    {
      title: '15. Changes',
      paragraphs: [
        'This data protection notice is updated when changes in the website, services, technology or legal requirements make an update necessary. The version published on this page applies at the time of access.',
      ],
    },
  ],
};
